/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDuplicateRecord';
const ActionDuplicateRecordManager: ActionManagerFn<'ActionDuplicateRecord'> = (
  platform: Platform,
): ActionManager<'ActionDuplicateRecord'> => {
  return {
    ...baseActionManager(platform, 'ActionDuplicateRecord'),
    isChildAllowed: (page, item, childType) =>
      (['ActionSet'] as ActionItemType[]).includes(childType as ActionItemType),
    defaultValue,
    defaultChildren: ['ActionSet'],
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      markAsDirty: {
        label: 'Mark As Dirty',
        type: 'boolean',
      },
      addPosition: {
        label: 'Add Position',
        type: 'select',
        options: {
          addTop: 'Add at the Top',
          addBottom: 'Add at the Bottom',
          addAfter: 'Next to Current Record',
          addBefore: 'Previous to Current Record',
        },
      },
    },
    name: 'Duplicate Record',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'clone' },
    description: `**Overview**<br/>
The Duplicate Record action is a data manipulation component designed to clone an existing record within a Data Store. It creates a copy of the "current" record (typically the one selected in a grid or active in a context) and inserts it as a new entry into the same store.<br/><br/>
This component functions as a logic container that strictly permits ActionSet components as children. This architecture allows developers to not only duplicate the record but also immediately override specific attributes with new values during the cloning process (e.g., clearing a "Status" field or generating a new "Name"). It offers precise control over where the new record is injected relative to the original.

**Key Features**<br/>
* **Strict Child Hierarchy:** Enforces ActionSet as the only allowed child component (isChildAllowed), ensuring nested logic is exclusively dedicated to modifying the attributes of the newly cloned record.
* **Positioning Control:** Features an addPosition property that allows builders to dictate where the new record appears: at the top, at the bottom, or immediately before/after the original record.
* **State Management:** Includes a markAsDirty boolean toggle. If enabled, the new record is flagged as changed locally, ensuring it will be included in the next database synchronization even if the user doesn't manually edit it.
* **Broad Store Compatibility:** Supports interaction with CloudDataStore, DataStore, LocalDataStore, and TransientDataStore.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store containing the record to be duplicated.
* **Mark As Dirty:** A boolean property.
  * *Function:* Determines if the cloned record is treated as a pending change (dirty) or a clean entry.
* **Add Position:** A select property defining the insertion index.
  * *Options:* Add at the Top, Add at the Bottom, Next to Current Record, Previous to Current Record.

**Use Cases**<br/>
* **Template Usage:** Selecting a "Template" record and duplicating it to create a new working record, then overriding key fields via child ActionSet items.
* **Data Entry Speed:** "Clone Row" functionality where users create multiple similar entries quickly by duplicating and adjusting only a few fields.`,
  };
};

export default ActionDuplicateRecordManager;
